// Enhanced FunctionForm.tsx with Robust Focus Management + Help Panel
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import '../companyManagement/CompanyManagement.css';
import '../companyManagement/CompanyFormOverrides.css';
import { UserSelection } from './UserSelection';
import { FunctionsApiService } from './apiService';
import { Function, Company, User, FunctionsManagementProps } from './types';

interface FunctionFormProps extends FunctionsManagementProps {
  editingFunction?: Function | null;
  companies: Company[];
  selectedCompany?: Company | null;
  isLoadingCompanies: boolean;
  onBack: () => void;
  onSuccess: () => void;
}

// ─── Help Panel Content ─────────────────────────────────────────────
interface HelpItem {
  title: string;
  description: string;
  tip?: string;
}

const HELP_CONTENT: Record<string, HelpItem> = {
  default: {
    title: 'Division Setup',
    description: 'Use this form to create or update a division within a company. Divisions define the organisational structure used to route gate pass approvals.',
    tip: 'All fields marked with * are required. Click on any field to see specific guidance.'
  },
  functionName: {
    title: 'Division Name',
    description: 'Enter a clear, descriptive name for the division. This will be visible when users select a division during gate pass creation.',
    tip: 'Use the official department or division name as recognised within the company (e.g. "Operations", "Engineering", "HSE").'
  },
  functionDescription: {
    title: 'Division Description',
    description: 'Provide a brief description of the division\'s responsibilities, scope of work, or area of operations within the company.',
    tip: 'A good description helps administrators and approvers understand the division\'s role quickly.'
  },
  companyId: {
    title: 'Company',
    description: 'Select the company this division belongs to. All approvers and reviewers will be drawn from the selected company\'s user list.',
    tip: 'The company cannot be changed after the division is created. Ensure you select the correct company before proceeding.'
  },
  approvers: {
    title: 'Approvers',
    description: 'Assign users who are authorised to approve gate pass requests submitted under this division. Only users with the "Company Approver" role are available for selection — roles can be assigned via "Manage Users".',
    tip: 'If no users appear, ensure the company has users with the Company Approver role assigned.'
  },
  reviewers: {
    title: 'Reviewers',
    description: 'Assign users who can review and provide feedback on gate pass requests before final approval. Only users with the "Company Reviewer" role are available for selection.',
    tip: 'Reviewers can endorse or flag concerns but do not have final approval authority. This step is optional.'
  }
};

export const FunctionForm: React.FC<FunctionFormProps> = ({
  editingFunction,
  companies,
  selectedCompany,
  isLoadingCompanies,
  getAccessToken,
  onStatusUpdate,
  onBack,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    functionName: '',
    functionDescription: '',
    companyId: selectedCompany?.companyId || 0,
    approverIds: [] as number[],
    reviewerIds: [] as number[]
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyUsers, setCompanyUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // ─── Role-filtered user lists ──────────────────────────────────────
  // Helper: check if a user's comma-separated role string contains a target role
  const userHasRole = (user: User, targetRole: string): boolean => {
    if (!user.role) return false;
    const roles = user.role.split(',').map(r => r.trim().toLowerCase());
    return roles.includes(targetRole.toLowerCase());
  };

  // Only show users with 'approver' role in the approvers selector
  const approverEligibleUsers = useMemo(() => {
    return companyUsers.filter(u => userHasRole(u, 'approver'));
  }, [companyUsers]);

  // Only show users with 'reviewer' role in the reviewers selector
  const reviewerEligibleUsers = useMemo(() => {
    return companyUsers.filter(u => userHasRole(u, 'reviewer'));
  }, [companyUsers]);
  const [apiService] = useState(() => new FunctionsApiService(getAccessToken));
  const [focusedField, setFocusedField] = useState<string>('default');
  const [showHelp, setShowHelp] = useState<boolean>(true);

  // Refs for focus management
  const modalRef = useRef<HTMLDivElement>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLInputElement>(null);
  const lastFocusableRef = useRef<HTMLButtonElement>(null);
  const approverFieldRef = useRef<HTMLDivElement>(null);
  const reviewerFieldRef = useRef<HTMLDivElement>(null);

  // Handle modal close - converted to useCallback for stable reference
  const handleClose = useCallback(() => {
    setFormData({
      functionName: '',
      functionDescription: '',
      companyId: selectedCompany?.companyId || 0,
      approverIds: [],
      reviewerIds: []
    });
    onBack();
  }, [selectedCompany?.companyId, onBack]);

  // Focus management utility
  const ensureFocusInModal = useCallback(() => {
    setTimeout(() => {
      if (modalRef.current && !modalRef.current.contains(document.activeElement)) {
        // Focus has escaped the modal, bring it back
        const focusableElements = modalRef.current.querySelectorAll(
          'input:not([disabled]), button:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
        );
        
        if (focusableElements.length > 0) {
          // Focus the first available element
          (focusableElements[0] as HTMLElement).focus();
        } else if (modalBodyRef.current) {
          // If no focusable elements, focus the modal body itself
          modalBodyRef.current.focus();
        }
      }
    }, 50);
  }, []);

  // Enhanced focus trap implementation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Tab') {
      const focusableElements = modalRef.current?.querySelectorAll(
        'input:not([disabled]), button:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
      );
      
      if (!focusableElements || focusableElements.length === 0) return;
      
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
      
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleClose();
    }
  }, [handleClose]);

  // Monitor focus changes and recapture if needed
  const handleFocusChange = useCallback(() => {
    // Small delay to allow focus to settle
    setTimeout(() => {
      if (modalRef.current && !modalRef.current.contains(document.activeElement)) {
        ensureFocusInModal();
      }
    }, 100);
  }, [ensureFocusInModal]);

  // Set up focus management
  useEffect(() => {
    // Disable background scrolling
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = '15px';
    
    // Add focus trap event listener
    document.addEventListener('keydown', handleKeyDown);
    
    // Monitor focus changes
    document.addEventListener('focusin', handleFocusChange);
    document.addEventListener('focusout', handleFocusChange);
    
    // Initial focus
    setTimeout(() => {
      if (firstFocusableRef.current) {
        firstFocusableRef.current.focus();
      }
    }, 100);

    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('focusin', handleFocusChange);
      document.removeEventListener('focusout', handleFocusChange);
    };
  }, [handleKeyDown, handleFocusChange]);

  // Handle modal overlay click
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  // Prevent event bubbling from modal content
  const handleModalContentClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  // Prevent scroll propagation to background
  const handleWheel = useCallback((e: React.WheelEvent) => {
    const modalBody = modalBodyRef.current;
    if (!modalBody) return;

    const { scrollTop, scrollHeight, clientHeight } = modalBody;
    const isScrollingDown = e.deltaY > 0;
    const isScrollingUp = e.deltaY < 0;

    if (isScrollingDown && scrollTop + clientHeight >= scrollHeight) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (isScrollingUp && scrollTop <= 0) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    e.stopPropagation();
  }, []);

  // Load users when company changes
  const loadCompanyUsers = useCallback(async (companyId: number) => {
    if (!companyId) {
      setCompanyUsers([]);
      return;
    }

    setIsLoadingUsers(true);
    
    const result = await apiService.loadUsersByCompany(companyId);
    
    if (result.success && result.data) {
      setCompanyUsers(result.data);
    } else {
      setCompanyUsers([]);
      onStatusUpdate({
        type: 'error',
        message: result.message || 'Failed to load company users'
      });
    }
    
    setIsLoadingUsers(false);
  }, [apiService, onStatusUpdate]);

  // Initialize form data
  useEffect(() => {
    if (editingFunction) {
      setFormData({
        functionName: editingFunction.functionName || '',
        functionDescription: editingFunction.functionDescription || '',
        companyId: editingFunction.companyId || selectedCompany?.companyId || 0,
        approverIds: editingFunction.approvers?.map(a => a.userId) || [],
        reviewerIds: editingFunction.reviewers?.map(r => r.userId) || []
      });
    } else {
      setFormData({
        functionName: '',
        functionDescription: '',
        companyId: selectedCompany?.companyId || 0,
        approverIds: [],
        reviewerIds: []
      });
    }
  }, [editingFunction, selectedCompany]);

  // Load users when company ID changes
  useEffect(() => {
    if (formData.companyId) {
      loadCompanyUsers(formData.companyId);
    }
  }, [formData.companyId, loadCompanyUsers]);

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const newValue = name === 'companyId' ? parseInt(value) || 0 : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue,
      ...(name === 'companyId' && newValue !== prev.companyId ? {
        approverIds: [],
        reviewerIds: []
      } : {})
    }));
  };

  // Handle approver selection change with explicit focus management
  const handleApproversChange = useCallback((selectedIds: number[]) => {
    setFormData(prev => ({
      ...prev,
      approverIds: selectedIds
    }));
    
    ensureFocusInModal();
  }, [ensureFocusInModal]);

  // Handle reviewer selection change with explicit focus management
  const handleReviewersChange = useCallback((selectedIds: number[]) => {
    setFormData(prev => ({
      ...prev,
      reviewerIds: selectedIds
    }));
    
    ensureFocusInModal();
  }, [ensureFocusInModal]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.functionName.trim()) {
      onStatusUpdate({
        type: 'error',
        message: 'Division name is required'
      });
      return;
    }

    if (!formData.functionDescription.trim()) {
      onStatusUpdate({
        type: 'error',
        message: 'Division description is required'
      });
      return;
    }

    // Only validate company selection when creating a new division
    if (!editingFunction && !formData.companyId) {
      onStatusUpdate({
        type: 'error',
        message: 'Please select a company'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      let result;
      
      if (editingFunction) {
        const updateData = {
          functionId: editingFunction.functionId,
          functionName: formData.functionName,
          functionDescription: formData.functionDescription,
          companyId: editingFunction.companyId,
          approverIds: formData.approverIds,
          reviewerIds: formData.reviewerIds
        };
        result = await apiService.updateFunction(updateData);
      } else {
        result = await apiService.createFunction(formData);
      }

      if (result.success) {
        onStatusUpdate({
          type: 'success',
          message: result.message || `Division ${editingFunction ? 'updated' : 'created'} successfully`
        });
        onSuccess();
      } else {
        onStatusUpdate({
          type: 'error',
          message: result.message || `Failed to ${editingFunction ? 'update' : 'create'} division`
        });
      }
    } catch (error) {
      onStatusUpdate({
        type: 'error',
        message: `Error ${editingFunction ? 'updating' : 'creating'} division`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get current help content based on focused field
  const currentHelp = HELP_CONTENT[focusedField] || HELP_CONTENT.default;

  return (
    <div 
      className="modal-overlay"
      onClick={handleOverlayClick}
      onWheel={(e) => e.preventDefault()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div 
        ref={modalRef}
        className={`modal-container ${showHelp ? 'company-form-with-help' : ''}`}
        onClick={handleModalContentClick}
        onWheel={handleWheel}
        onFocus={ensureFocusInModal}
      >
        <div className="modal-header">
          <h2 id="modal-title" className="modal-title">
            {editingFunction ? 'Edit Division' : 'Create Division'}
          </h2>
          <button 
            ref={lastFocusableRef}
            type="button" 
            className="modal-close-btn"
            onClick={handleClose}
            aria-label="Close modal"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="modal-content-with-help">
          {/* ─── Left: Form ─────────────────────────────────────────── */}
          <form onSubmit={handleSubmit} className="modal-form company-form-main">
            <div 
              ref={modalBodyRef}
              className="modal-body"
              tabIndex={-1}
              onFocus={ensureFocusInModal}
            >
              <div className="form-field" onFocus={() => setFocusedField('functionName')}>
                <label htmlFor="functionName" className="field-label">
                  Division Name <span className="required">*</span>
                </label>
                <input
                  ref={firstFocusableRef}
                  type="text"
                  id="functionName"
                  name="functionName"
                  value={formData.functionName}
                  onChange={handleInputChange}
                  placeholder="e.g. Operations, Engineering, Project Alpha"
                  className="field-input"
                  required
                  disabled={isSubmitting}
                  autoFocus
                />
              </div>

              <div className="form-field" onFocus={() => setFocusedField('functionDescription')}>
                <label htmlFor="functionDescription" className="field-label">
                  Division Description <span className="required">*</span>
                </label>
                <textarea
                  id="functionDescription"
                  name="functionDescription"
                  value={formData.functionDescription}
                  onChange={handleInputChange}
                  placeholder="Describe the responsibilities and scope of this division..."
                  className="field-textarea"
                  rows={4}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-field" onFocus={() => setFocusedField('companyId')}>
                <label htmlFor="companyId" className="field-label">
                  Company <span className="required">*</span>
                </label>
                {editingFunction ? (
                  <div className="field-input field-disabled">
                    {editingFunction.companyName || 
                     companies.find(c => c.companyId === editingFunction.companyId)?.companyName || 
                     selectedCompany?.companyName || 
                     'Unknown Company'}
                  </div>
                ) : selectedCompany ? (
                  <div className="field-input field-disabled">
                    {selectedCompany.companyName}
                    <input
                      type="hidden"
                      name="companyId"
                      value={selectedCompany.companyId}
                    />
                  </div>
                ) : (
                  <select
                    id="companyId"
                    name="companyId"
                    value={formData.companyId || ''}
                    onChange={handleInputChange}
                    className="field-select"
                    required
                    disabled={isSubmitting || isLoadingCompanies}
                  >
                    <option value="">Please select</option>
                    {companies.map(company => (
                      <option key={company.companyId} value={company.companyId}>
                        {company.companyName}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Approvers Section */}
              {formData.companyId && (
                <div ref={approverFieldRef} className="form-field" onFocus={() => setFocusedField('approvers')} onClick={() => setFocusedField('approvers')}>
                  <label className="field-label">
                    Approvers
                  </label>
                  <UserSelection
                    users={approverEligibleUsers}
                    selectedUserIds={formData.approverIds}
                    onSelectionChange={handleApproversChange}
                    label=""
                    placeholder="Search for approvers by name or email..."
                    isLoading={isLoadingUsers}
                    disabled={isSubmitting}
                    onDropdownClose={ensureFocusInModal}
                  />
                  <small className="field-help">
                    Only users assigned the "Company Approver" role are shown here
                  </small>
                </div>
              )}

              {/* Reviewers Section */}
              {formData.companyId && (
                <div ref={reviewerFieldRef} className="form-field" onFocus={() => setFocusedField('reviewers')} onClick={() => setFocusedField('reviewers')}>
                  <label className="field-label">
                    Reviewers
                  </label>
                  <UserSelection
                    users={reviewerEligibleUsers}
                    selectedUserIds={formData.reviewerIds}
                    onSelectionChange={handleReviewersChange}
                    label=""
                    placeholder="Search for reviewers by name or email..."
                    isLoading={isLoadingUsers}
                    disabled={isSubmitting}
                    onDropdownClose={ensureFocusInModal}
                  />
                  <small className="field-help">
                    Only users assigned the "Company Reviewer" role are shown here
                  </small>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                type="button" 
                className="btn-cancel"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn-submit"
                disabled={isSubmitting || isLoadingCompanies || isLoadingUsers}
              >
                {isSubmitting ? 
                  (editingFunction ? 'Updating...' : 'Creating...') : 
                  (editingFunction ? 'Update Division' : 'Create Division')
                }
              </button>
            </div>
          </form>

          {/* ─── Help Panel (collapsible with animation) ────────────── */}
          <div className={`company-help-panel ${showHelp ? 'help-panel-open' : 'help-panel-closed'}`}>
            <div className="help-panel-inner">
              <div className={`help-panel-card ${focusedField !== 'default' ? 'help-active' : ''}`}>
                <h4 className="help-card-title">{currentHelp.title}</h4>
                <p className="help-card-description">{currentHelp.description}</p>
                {currentHelp.tip && (
                  <div className="help-card-tip">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F3694A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    <span>{currentHelp.tip}</span>
                  </div>
                )}
              </div>

              <div className="help-panel-quickref">
                <h4 className="help-quickref-title">Quick Reference</h4>
                <ul className="help-quickref-list">
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>Each division must belong to a company</span>
                  </li>
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>Only users with matching roles appear in selection</span>
                  </li>
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>Assign roles to users via "Manage Users" first</span>
                  </li>
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>Company cannot be changed after creation</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Toggle strip — outer (right) edge */}
          <div className="help-toggle-strip">
            <button
              type="button"
              className="help-toggle-icon-btn"
              onClick={() => setShowHelp(!showHelp)}
              title={showHelp ? 'Hide Guide' : 'Show Guide'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </button>
            <button
              type="button"
              className="help-toggle-tab"
              onClick={() => setShowHelp(!showHelp)}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {showHelp
                  ? <polyline points="9 18 15 12 9 6"/>
                  : <polyline points="15 18 9 12 15 6"/>
                }
              </svg>
              <span>{showHelp ? 'Hide Guide' : 'Show Guide'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};